import { useState, useRef, useEffect, useId, useCallback, useLayoutEffect } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import styles from './Tooltip.module.css'

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

/** Which side the card actually landed on, after any flip. */
type TooltipSide = 'above' | 'below' | 'left' | 'right'

const SIDE_CLASS: Record<TooltipSide, string> = {
  above: styles.cardAbove,
  below: styles.cardBelow,
  left: styles.cardLeft,
  right: styles.cardRight,
}

export interface TooltipProps {
  /** Body of the tooltip card. */
  content: ReactNode
  /** Trigger element. Wrapped in an inline span that owns hover/focus listeners. */
  children: ReactNode
  /**
   * Preferred placement. Auto-flips to the opposite side when there is not
   * enough viewport space. 'left'/'right' are what a collapsed icon rail wants:
   * the card sits beside the trigger, vertically centred on it. Default: 'top'.
   */
  placement?: TooltipPlacement
  /** Delay (ms) before the tooltip opens on hover/focus. Default: 200. */
  delay?: number
  /** Skip rendering the tooltip and render only the trigger. */
  disabled?: boolean
  /** Cap on tooltip width in px. Default: 320. */
  maxWidth?: number
  /** Class name applied to the trigger wrapper span. */
  className?: string
  /**
   * A click or tap also opens the card, and pins it: it stays open (leaving
   * the trigger no longer closes it) until a click outside, Escape, or a second
   * click. For cards worth reading or acting on — and for Safari and phones,
   * which don't focus a button on click, so hover/focus alone never opens.
   */
  openOnClick?: boolean
}

export function Tooltip({
  content,
  children,
  placement = 'top',
  delay = 200,
  disabled,
  maxWidth = 320,
  className,
  openOnClick,
}: TooltipProps) {
  const [open, setOpen] = useState(false)
  const [pinned, setPinned] = useState(false)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const openTimerRef = useRef<number | null>(null)
  const tooltipId = useId()
  const [pos, setPos] = useState<{ top: number; left: number; side: TooltipSide }>({
    top: 0,
    left: 0,
    side: 'above',
  })

  const cancelOpen = useCallback(() => {
    if (openTimerRef.current !== null) {
      window.clearTimeout(openTimerRef.current)
      openTimerRef.current = null
    }
  }, [])

  const close = useCallback(() => {
    cancelOpen()
    setOpen(false)
    setPinned(false)
  }, [cancelOpen])

  // Hover and focus leaving the trigger close an unpinned card only.
  const softClose = useCallback(() => {
    if (!pinned) close()
  }, [pinned, close])

  const togglePinned = useCallback(() => {
    if (pinned) {
      close()
      return
    }
    cancelOpen()
    setOpen(true)
    setPinned(true)
  }, [pinned, close, cancelOpen])

  const requestOpen = useCallback(() => {
    /* v8 ignore next -- redundant guard; the control is already disabled so this handler is never invoked */
    if (disabled || content == null || content === false) return
    cancelOpen()
    if (delay <= 0) {
      setOpen(true)
      return
    }
    openTimerRef.current = window.setTimeout(() => {
      setOpen(true)
      openTimerRef.current = null
    }, delay)
  }, [cancelOpen, content, delay, disabled])

  // Cleanup any pending open timer on unmount.
  useEffect(() => () => { cancelOpen() }, [cancelOpen])

  // Position the portal card. Measure the trigger, pick a side based on the
  // viewport space available, then centre on the trigger along the other axis
  // and clamp inside an 8px viewport margin. The chosen side's class supplies
  // the transform that puts the right edge of the card at the anchor point.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    /* v8 ignore next -- layout fallback; offsetWidth/Height is numeric once mounted in a real browser */
    const cardHeight = cardRef.current?.offsetHeight ?? 0
    /* v8 ignore next -- layout fallback; offsetWidth/Height is numeric once mounted in a real browser */
    const cardWidth = cardRef.current?.offsetWidth ?? maxWidth
    const margin = 8

    const clamp = (ideal: number, half: number, limit: number) => {
      const min = margin + half
      const max = limit - margin - half
      // Card taller/wider than the viewport allows — centre it and accept the overflow.
      return min > max ? limit / 2 : Math.max(min, Math.min(ideal, max))
    }

    if (placement === 'left' || placement === 'right') {
      const spaceLeft = rect.left
      const spaceRight = window.innerWidth - rect.right
      const wantRight = placement === 'right'
      const toRight = wantRight
        ? !(spaceRight < cardWidth + margin && spaceLeft > spaceRight)
        : (spaceLeft < cardWidth + margin && spaceRight > spaceLeft)

      setPos({
        top: clamp(rect.top + rect.height / 2, cardHeight / 2, window.innerHeight),
        left: toRight ? rect.right + margin : rect.left - margin,
        side: toRight ? 'right' : 'left',
      })
      return
    }

    const wantAbove = placement === 'top'
    const spaceAbove = rect.top
    const spaceBelow = window.innerHeight - rect.bottom
    const above = wantAbove
      ? !(spaceAbove < cardHeight + margin && spaceBelow > spaceAbove)
      : (spaceBelow < cardHeight + margin && spaceAbove > spaceBelow)

    setPos({
      top: above ? rect.top - margin : rect.bottom + margin,
      left: clamp(rect.left + rect.width / 2, cardWidth / 2, window.innerWidth),
      side: above ? 'above' : 'below',
    })
  }, [open, placement, maxWidth, content])

  // Close on outside pointer-down (touch tap-away dismisses).
  useEffect(() => {
    if (!open) return
    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Node
      if (triggerRef.current && triggerRef.current.contains(target)) return
      if (cardRef.current && cardRef.current.contains(target)) return
      close()
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => document.removeEventListener('pointerdown', handlePointerDown)
  }, [open, close])

  // Close on Escape so keyboard users aren't trapped looking at the card.
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open, close])

  // Close on scroll — the position is captured at open time so any
  // movement would leave the card stranded.
  useEffect(() => {
    if (!open || !triggerRef.current) return
    const initialTop = triggerRef.current.getBoundingClientRect().top
    function handleScroll() {
      /* v8 ignore next -- ref is always attached while the component is mounted */
      if (!triggerRef.current) return
      const currentTop = triggerRef.current.getBoundingClientRect().top
      if (Math.abs(currentTop - initialTop) > 4) close()
    }
    window.addEventListener('scroll', handleScroll, true)
    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [open, close])

  if (disabled || content == null || content === false) {
    return <>{children}</>
  }

  const wrapperClasses = [styles.trigger, className].filter(Boolean).join(' ')

  return (
    <>
      <span
        ref={triggerRef}
        className={wrapperClasses}
        onMouseEnter={requestOpen}
        onMouseLeave={softClose}
        onFocus={requestOpen}
        onBlur={softClose}
        onClick={openOnClick ? togglePinned : undefined}
        aria-describedby={open ? tooltipId : undefined}
      >
        {children}
      </span>

      {open && createPortal(
        <div
          ref={cardRef}
          id={tooltipId}
          role="tooltip"
          className={`${styles.card} ${SIDE_CLASS[pos.side]}`}
          style={{
            top: pos.top,
            left: pos.left,
            maxWidth,
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  )
}
